/* Atar — global.js · spec §10 · ES2020, zero deps, one IIFE per component,
   progressive enhancement (no-JS falls back to native forms/pages).
   Hooks consumed: drawers [data-drawer="name"] roots (rendered hidden) +
   [data-drawer-open="name"] / [data-drawer-close] / [data-drawer-overlay]
   (JS toggles [hidden]+.is-open; trap/Esc/inert/aria/scroll-lock included) ·
   cart triggers [data-cart-open] (keep as /cart links), badges
   [data-cart-count] (aria-live, hidden at 0), <cart-drawer> auto-created,
   lines qty-input[data-line-key] + [data-cart-remove][data-line-key]; forms
   posting to /cart/add intercepted (opt-out data-ajax-cart="off"), errors →
   [data-form-error] · <qty-input> buttons name=minus/plus · header
   [data-header]/.header gets .is-scrolled >12px · <details> animated
   (opt-out data-no-animate) · <facet-form> wraps filter form, debounced
   auto-submit (data-debounce, 400ms) · gallery [data-gallery] >
   [data-gallery-item|-thumb][data-media-id] · [data-form-success=
   "customer_posted|contact_posted"] revealed when that query param="true" ·
   [data-animate] gets .is-revealed from one shared IntersectionObserver (CSS
   owns duration/stagger; opt-out data-no-animate shows at once, as reduced
   motion and no-JS do; Atar.reveal(root) re-scans, wired to
   shopify:section:load) · strings via window.Atar.strings (§5 keys) or
   #atar-strings JSON (EN fallbacks below); window.Atar may carry
   { moneyFormat, currency, routes }. */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.__atarGlobal) return;
  W.__atarGlobal = true;

  /* Core utilities / namespace */
  (() => {
    const A = (W.Atar = W.Atar || {});
    A.root = (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/';

    const FALLBACK = {
      'general.loading': 'Loading…',
      'cart.title': 'Your cart',
      'cart.empty': 'Your cart is empty.',
      'cart.continue': 'Continue shopping',
      'cart.subtotal': 'Subtotal',
      'cart.checkout': 'Checkout',
      'cart.remove': 'Remove',
      'cart.shipping_note': 'Shipping and taxes are calculated at checkout.',
      'cart.item_singular': 'item',
      'cart.item_plural': 'items',
      'product.quantity': 'Quantity',
      'product.unavailable': 'Unavailable',
      'accessibility.close_dialog': 'Close dialog',
      'accessibility.decrease_qty': 'Decrease quantity',
      'accessibility.increase_qty': 'Increase quantity'
    };
    let injected = null;
    const block = D.getElementById('atar-strings');
    try { injected = JSON.parse(block && block.textContent); } catch {}
    A.strings = Object.assign({}, FALLBACK, injected, A.strings);
    A.t = (key) => A.strings[key] || key;

    A.money = (cents) => {
      const n = (cents || 0) / 100;
      if (typeof A.moneyFormat === 'string' && A.moneyFormat.includes('{{')) {
        return A.moneyFormat.replace(
          /\{\{\s*amount\w*\s*\}\}/,
          n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        );
      }
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: A.currency || 'USD' }).format(n);
    };

    A.reducedMotion = () => W.matchMedia('(prefers-reduced-motion: reduce)').matches;

    A.ready = (fn) => (D.readyState === 'loading'
      ? D.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn());

    A.el = (tag, attrs, ...kids) => {
      const node = D.createElement(tag);
      for (const k in attrs || {}) {
        const v = attrs[k];
        if (v == null || v === false) continue;
        if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v; /* static SVG only — never user/cart data */
        else node.setAttribute(k, v === true ? '' : v);
      }
      kids.flat().forEach((c) => { if (c != null) node.append(c); });
      return node;
    };

    A.svg = {
      close: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>',
      minus: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 12h16"/></svg>',
      plus: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M12 4v16M4 12h16"/></svg>'
    };

    let region = null;
    A.announce = (msg) => {
      if (!region) {
        region = D.createElement('div');
        region.setAttribute('role', 'status');
        region.style.cssText = 'position:fixed;width:1px;height:1px;overflow:hidden;clip-path:inset(50%)';
        D.body.append(region);
      }
      region.textContent = '';
      requestAnimationFrame(() => { region.textContent = msg; });
    };
  })();

  /* Drawers: dialog semantics, focus trap, Esc, inert background */
  (() => {
    const A = W.Atar;
    const stack = [];
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const focusables = (root) => [...root.querySelectorAll(FOCUSABLE)].filter((n) => n.getClientRects().length);

    const setExpanded = (root, on) => {
      const name = root.getAttribute('data-drawer');
      if (!name) return;
      const sel = `[data-drawer-open="${name}"]` + (name === 'cart' ? ',[data-cart-open]' : '');
      D.querySelectorAll(sel).forEach((o) => o.setAttribute('aria-expanded', on ? 'true' : 'false'));
    };

    let _scrim = null;
    const scrim = () => {
      if (!_scrim) {
        _scrim = D.createElement('div');
        _scrim.className = 'scrim';
        _scrim.setAttribute('data-drawer-overlay', '');
        D.body.appendChild(_scrim);
      }
      return _scrim;
    };

    const open = (root, opener) => {
      if (!root || root.classList.contains('is-open')) return;
      root._token = (root._token || 0) + 1;
      if (!root.hasAttribute('role')) root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.hidden = false;
      void root.offsetWidth;
      root.classList.add('is-open');
      scrim().classList.add('is-open');
      const inerted = [];
      [...D.body.children].forEach((child) => {
        if (child === root || child.contains(root) || child.inert || child === _scrim) return;
        if (/^(SCRIPT|STYLE|LINK)$/.test(child.tagName)) return;
        child.inert = true;
        inerted.push(child);
      });
      if (!stack.length) {
        A._prevOverflow = D.documentElement.style.overflow;
        D.documentElement.style.overflow = 'hidden';
      }
      stack.push({ root, opener: opener || D.activeElement, inerted });
      setExpanded(root, true);
      const target =
        root.querySelector('[data-autofocus]') ||
        root.querySelector('input[type="search"],input[name="q"]') ||
        focusables(root)[0] || root;
      if (target === root) root.tabIndex = -1;
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
      root.dispatchEvent(new CustomEvent('atar:drawer:open', { bubbles: true }));
    };

    const close = (root) => {
      const i = stack.findIndex((s) => s.root === root);
      if (i === -1) return;
      const [entry] = stack.splice(i, 1);
      entry.inerted.forEach((n) => { n.inert = false; });
      root.classList.remove('is-open');
      if (!stack.length && _scrim) _scrim.classList.remove('is-open');
      setExpanded(root, false);
      const token = ++root._token;
      const finish = () => {
        if (root._token === token && !root.classList.contains('is-open')) root.hidden = true;
      };
      if (A.reducedMotion()) finish(); else setTimeout(finish, 300);
      if (!stack.length) D.documentElement.style.overflow = A._prevOverflow || '';
      if (entry.opener && entry.opener.focus) entry.opener.focus();
    };

    D.addEventListener('keydown', (e) => {
      const top = stack[stack.length - 1];
      if (!top) return;
      if (e.key === 'Escape') { e.preventDefault(); close(top.root); return; }
      if (e.key !== 'Tab') return;
      const list = focusables(top.root);
      if (!list.length) { e.preventDefault(); return; }
      const first = list[0], last = list[list.length - 1], active = D.activeElement;
      if (e.shiftKey && (active === first || !top.root.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !top.root.contains(active))) {
        e.preventDefault(); first.focus();
      }
    });

    D.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        const root = D.querySelector(`[data-drawer="${opener.getAttribute('data-drawer-open')}"]`);
        if (root) {
          e.preventDefault();
          if (root.classList.contains('is-open')) close(root); else open(root, opener);
        }
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        const root = closer.closest('[data-drawer]');
        if (root) { e.preventDefault(); close(root); }
        return;
      }
      if (e.target instanceof Element &&
          (e.target.matches('[data-drawer].is-open') || e.target.matches('[data-drawer-overlay]'))) {
        const root = e.target.closest('[data-drawer]') ||
          (stack.length ? stack[stack.length - 1].root : null);
        if (root) close(root);
      }
    });

    A.ready(() => {
      D.querySelectorAll('[data-drawer]').forEach((d) => {
        if (!d.classList.contains('is-open')) d.hidden = true;
      });
    });

    A.drawer = { open, close };
  })();

  /* Sticky header: .is-scrolled drives the backdrop-blur in CSS */
  (() => {
    W.Atar.ready(() => {
      const header = D.querySelector('[data-header]') || D.querySelector('.header');
      if (!header) return;
      let ticking = false;
      const update = () => {
        header.classList.toggle('is-scrolled', W.scrollY > 12);
        ticking = false;
      };
      W.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    });
  })();

  /* Accordion: details/summary animation + aria-expanded */
  (() => {
    const A = W.Atar;
    const animate = (details, from, to, done) => {
      details.style.overflow = 'hidden';
      const anim = details.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: 220, easing: 'cubic-bezier(.22,.61,.36,1)' }
      );
      anim.onfinish = anim.oncancel = () => {
        details.style.overflow = '';
        delete details.dataset.animating;
        if (done) done();
      };
    };

    D.addEventListener('click', (e) => {
      const summary = e.target.closest('summary');
      if (!summary) return;
      const details = summary.closest('details');
      if (!details || details.dataset.noAnimate != null) return;
      if (A.reducedMotion() || typeof details.animate !== 'function') return;
      e.preventDefault();
      if (details.dataset.animating) return;
      details.dataset.animating = '1';
      const from = details.offsetHeight;
      if (details.open) {
        animate(details, from, summary.offsetHeight, () => { details.open = false; });
      } else {
        details.open = true;
        animate(details, from, details.offsetHeight);
      }
    });

    D.addEventListener('toggle', (e) => {
      const d = e.target;
      if (d instanceof HTMLDetailsElement) {
        const s = d.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
      }
    }, true);

    A.ready(() => {
      D.querySelectorAll('details > summary').forEach((s) => {
        s.setAttribute('aria-expanded', s.parentElement.open ? 'true' : 'false');
      });
    });
  })();

  /* <qty-input>: stepper around a native number input */
  (() => {
    class QtyInput extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('button[name="minus"],[data-qty-minus],button[name="plus"],[data-qty-plus]');
          if (!btn || !this.contains(btn)) return;
          e.preventDefault();
          this.step(btn.matches('button[name="plus"],[data-qty-plus]') ? 1 : -1);
        });
        const input = this.input;
        if (input) input.addEventListener('change', () => this.clamp());
        this.sync();
      }
      get input() { return this.querySelector('input'); }
      get min() {
        const i = this.input;
        return i && i.min !== '' ? parseInt(i.min, 10) : 1;
      }
      bound(v) {
        const i = this.input;
        const max = i.max !== '' ? parseInt(i.max, 10) : Infinity;
        return Math.min(max, Math.max(this.min, v));
      }
      step(dir) {
        const input = this.input;
        if (!input) return;
        const inc = parseInt(input.step, 10) || 1;
        const next = this.bound((parseInt(input.value, 10) || 0) + dir * inc);
        if (next == input.value) return;
        input.value = next;
        this.sync();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      clamp() {
        const input = this.input;
        input.value = this.bound(parseInt(input.value, 10) || 1);
        this.sync();
      }
      sync() {
        const input = this.input;
        if (!input) return;
        const minus = this.querySelector('button[name="minus"],[data-qty-minus]');
        if (minus) minus.disabled = (parseInt(input.value, 10) || 0) <= this.min;
      }
    }
    if (!customElements.get('qty-input')) customElements.define('qty-input', QtyInput);
  })();

  /* Cart: <cart-drawer>, /cart.js render, add/change with fallbacks */
  (() => {
    const A = W.Atar;
    const { t, money, el } = A;
    const cartHref = () => (A.routes && A.routes.cart) || (A.root + 'cart');
    const req = (path, opts) =>
      fetch(A.root + path, Object.assign({ headers: { Accept: 'application/json' } }, opts));

    const updateCounts = (cart) => {
      D.querySelectorAll('[data-cart-count]').forEach((n) => {
        if (!n.hasAttribute('aria-live')) n.setAttribute('aria-live', 'polite');
        n.textContent = cart.item_count;
        n.toggleAttribute('hidden', !cart.item_count);
        n.classList.toggle('is-empty', !cart.item_count);
      });
    };
    const announceCount = (cart) => {
      A.announce(cart.item_count + ' ' + t(cart.item_count === 1 ? 'cart.item_singular' : 'cart.item_plural'));
    };
    const imgSrc = (src, w) => {
      try {
        const u = new URL(src, location.origin);
        u.searchParams.set('width', w);
        return u.href;
      } catch { return src; }
    };

    class CartDrawer extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.classList.add('drawer', 'drawer--end', 'cart-drawer');
        this.setAttribute('data-drawer', 'cart');
        this.setAttribute('aria-label', t('cart.title'));
        this.hidden = true;
        this.body = el('div', { class: 'drawer__body cart-drawer__body', text: t('general.loading') });
        this.footer = el('footer', { class: 'drawer__footer cart-drawer__footer', hidden: true });
        this.append(
          el('header', { class: 'drawer__header' },
            el('h2', { class: 'drawer__title', text: t('cart.title') }),
            el('button', { type: 'button', class: 'drawer__close', 'data-drawer-close': true, 'aria-label': t('accessibility.close_dialog'), html: A.svg.close })
          ),
          this.body,
          this.footer
        );
        this.addEventListener('atar:drawer:open', () => { this.refresh().catch(() => {}); });
        this.addEventListener('change', (e) => {
          const q = e.target.closest && e.target.closest('qty-input[data-line-key]');
          if (!q || !this.contains(q)) return;
          const input = q.querySelector('input');
          this.change(q.getAttribute('data-line-key'), Math.max(0, parseInt(input.value, 10) || 0));
        });
        this.addEventListener('click', (e) => {
          const r = e.target.closest('[data-cart-remove]');
          if (r && this.contains(r)) {
            e.preventDefault();
            this.change(r.getAttribute('data-line-key'), 0);
          }
        });
      }
      open() { A.drawer.open(this); }
      refresh() {
        if (this._loading) return this._loading;
        this._loading = req('cart.js')
          .then((r) => { if (!r.ok) throw 0; return r.json(); })
          .then((cart) => { this.render(cart); updateCounts(cart); return cart; })
          .finally(() => { this._loading = null; });
        return this._loading;
      }
      change(key, qty) {
        this.setAttribute('aria-busy', 'true');
        this._chain = (this._chain || Promise.resolve())
          .then(() => req('cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty })
          }))
          .then((r) => { if (!r.ok) throw 0; return r.json(); })
          .then((cart) => { this.render(cart); updateCounts(cart); announceCount(cart); })
          .catch(() => this.refresh().catch(() => location.assign(cartHref())))
          .finally(() => this.removeAttribute('aria-busy'));
        return this._chain;
      }
      render(cart) {
        const body = this.body;
        body.textContent = '';
        if (!cart.item_count) {
          this.footer.hidden = true;
          body.append(
            el('div', { class: 'cart-drawer__empty' },
              el('p', { text: t('cart.empty') }),
              el('button', { type: 'button', class: 'btn', 'data-drawer-close': true, text: t('cart.continue') })
            )
          );
          return;
        }
        const list = el('ul', { class: 'cart-lines', role: 'list' });
        cart.items.forEach((item) => {
          const meta = (item.options_with_values || [])
            .filter((o) => o.value !== 'Default Title')
            .map((o) => o.value)
            .join(' · ');
          list.append(
            el('li', { class: 'cart-line' },
              item.image
                ? el('a', { class: 'cart-line__media', href: item.url, tabindex: '-1', 'aria-hidden': 'true' },
                    el('img', { src: imgSrc(item.image, 180), alt: '', width: '90', height: '120', loading: 'lazy' }))
                : null,
              el('div', { class: 'cart-line__info' },
                item.vendor ? el('p', { class: 'cart-line__vendor', text: item.vendor }) : null,
                el('a', { class: 'cart-line__title', href: item.url }, el('bdi', { text: item.product_title })),
                meta ? el('p', { class: 'cart-line__meta', text: meta }) : null,
                el('div', { class: 'cart-line__actions' },
                  el('qty-input', { class: 'qty', 'data-line-key': item.key },
                    el('button', { type: 'button', name: 'minus', 'aria-label': t('accessibility.decrease_qty'), html: A.svg.minus }),
                    el('input', { type: 'number', inputmode: 'numeric', min: '1', value: String(item.quantity), 'aria-label': t('product.quantity') }),
                    el('button', { type: 'button', name: 'plus', 'aria-label': t('accessibility.increase_qty'), html: A.svg.plus })
                  ),
                  el('button', { type: 'button', class: 'cart-line__remove', 'data-cart-remove': true, 'data-line-key': item.key, 'aria-label': t('cart.remove') + ' — ' + item.product_title, text: t('cart.remove') })
                )
              ),
              el('p', { class: 'cart-line__price', text: money(item.final_line_price) })
            )
          );
        });
        body.append(list);
        const f = this.footer;
        f.textContent = '';
        f.hidden = false;
        f.append(
          el('div', { class: 'cart-drawer__subtotal' },
            el('span', { text: t('cart.subtotal') }),
            el('strong', { text: money(cart.total_price) })
          ),
          el('p', { class: 'cart-drawer__note', text: t('cart.shipping_note') }),
          el('a', { class: 'btn btn--full', href: '/checkout', text: t('cart.checkout') }),
          el('button', { type: 'button', class: 'btn btn--ghost btn--full', 'data-drawer-close': true, text: t('cart.continue') })
        );
      }
    }
    if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

    const getDrawer = () => {
      let d = D.querySelector('cart-drawer');
      if (!d) {
        d = D.createElement('cart-drawer');
        D.body.append(d);
      }
      return d;
    };

    D.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cart-open]');
      if (!trigger) return;
      e.preventDefault();
      getDrawer().open();
    });

    D.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      let path;
      try { path = new URL(form.action, location.origin).pathname; } catch { return; }
      if (!/\/cart\/add$/.test(path) || form.dataset.ajaxCart === 'off') return;
      e.preventDefault();
      addFromForm(form);
    });

    async function addFromForm(form) {
      const btn = form.querySelector('[type="submit"]');
      const saved = btn ? [...btn.childNodes] : [];
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = t('general.loading');
      }
      const errBox = form.querySelector('[data-form-error]');
      if (errBox) { errBox.hidden = true; errBox.textContent = ''; }
      try {
        const r = await req('cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!r.ok) {
          const err = await r.json().catch(() => null);
          const msg = (err && (err.description || err.message)) || t('product.unavailable');
          if (errBox) {
            if (!errBox.hasAttribute('role')) errBox.setAttribute('role', 'alert');
            errBox.textContent = msg;
            errBox.hidden = false;
          } else {
            A.announce(msg);
          }
          return;
        }
        const drawer = getDrawer();
        const cart = await drawer.refresh();
        announceCount(cart);
        drawer.open();
      } catch {
        form.submit();
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.textContent = '';
          btn.append(...saved);
        }
      }
    }
  })();

  /* <facet-form>: debounced auto-submit for collection filters */
  (() => {
    class FacetForm extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        const form = this.querySelector('form');
        if (!form) return;
        const delay = parseInt(this.getAttribute('data-debounce'), 10) || 400;
        let timer;
        const submit = () => {
          this.classList.add('is-loading');
          if (form.requestSubmit) form.requestSubmit(); else form.submit();
        };
        const schedule = (ms) => {
          clearTimeout(timer);
          timer = setTimeout(submit, ms);
        };
        form.addEventListener('change', () => schedule(delay));
        form.addEventListener('input', (e) => {
          if (e.target.matches('input[type="number"],input[type="text"],input[type="search"]')) {
            schedule(delay * 2);
          }
        });
      }
    }
    if (!customElements.get('facet-form')) customElements.define('facet-form', FacetForm);
  })();

  /* Product gallery: thumb switching */
  (() => {
    D.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      const gallery = thumb.closest('[data-gallery]');
      if (!gallery) return;
      e.preventDefault();
      const id = thumb.getAttribute('data-media-id');
      gallery.querySelectorAll('[data-gallery-item]').forEach((item) => {
        item.toggleAttribute('hidden', item.getAttribute('data-media-id') !== id);
      });
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((b) => {
        b.setAttribute('aria-current', b === thumb ? 'true' : 'false');
      });
    });
  })();

  /* Newsletter/contact inline success via query param */
  (() => {
    W.Atar.ready(() => {
      const params = new URLSearchParams(location.search);
      D.querySelectorAll('[data-form-success]').forEach((box) => {
        const key = box.getAttribute('data-form-success') || 'customer_posted';
        if (params.get(key) !== 'true') return;
        box.hidden = false;
        const form = box.closest('form') || (box.parentElement && box.parentElement.querySelector('form'));
        if (form) form.classList.add('form--posted');
        box.tabIndex = -1;
        box.focus();
      });
    });
  })();

  /* Variant picker: option pills → hidden id input, price wrappers, ATC state, ?variant= URL */
  (() => {
    const A = W.Atar;
    A.ready(() => {
      D.querySelectorAll('[data-product-form]').forEach((form) => {
        const section = form.closest('.shopify-section') || form.closest('section') || D;
        const json = section.querySelector('[data-variant-json]');
        const picker = section.querySelector('[data-variant-picker]');
        if (!json || !picker) return;
        let variants;
        try { variants = JSON.parse(json.textContent); } catch { return; }
        if (!Array.isArray(variants) || !variants.length) return;
        const texts = {
          add: form.getAttribute('data-text-add') || A.t('product.add_to_cart'),
          soldout: form.getAttribute('data-text-soldout') || A.t('product.sold_out'),
          unavailable: form.getAttribute('data-text-unavailable') || A.t('product.unavailable')
        };
        const priceHTML = (v) => {
          if (v.compare_at_price > v.price) {
            return '<span class="price price--on-sale"><s class="price__compare">' +
              A.money(v.compare_at_price) + '</s> <span class="price__sale">' +
              A.money(v.price) + '</span></span>';
          }
          return '<span class="price">' + A.money(v.price) + '</span>';
        };
        const update = () => {
          const sel = [...picker.querySelectorAll('fieldset')].map((f) => {
            const c = f.querySelector('[data-option-value]:checked');
            return c ? c.value : null;
          });
          const v = variants.find((x) =>
            sel.every((o, i) => o === null || [x.option1, x.option2, x.option3][i] === o));
          const btns = section.querySelectorAll('[data-add-to-cart]');
          const labels = section.querySelectorAll('[data-add-to-cart-text]');
          if (!v) {
            btns.forEach((b) => { b.disabled = true; });
            labels.forEach((l) => { l.textContent = texts.unavailable; });
            return;
          }
          section.querySelectorAll('input[data-variant-id]').forEach((i) => { i.value = v.id; });
          section.querySelectorAll('[data-price-wrapper]').forEach((w) => { w.innerHTML = priceHTML(v); });
          btns.forEach((b) => { b.disabled = !v.available; });
          labels.forEach((l) => { l.textContent = v.available ? texts.add : texts.soldout; });
          try {
            const u = new URL(location.href);
            u.searchParams.set('variant', v.id);
            history.replaceState({}, '', u);
          } catch { /* sandboxed contexts */ }
        };
        picker.addEventListener('change', (e) => {
          if (e.target instanceof Element && e.target.matches('[data-option-value]')) update();
        });
      });
    });
  })();

  /* Reveals: [data-animate] → .is-revealed on scroll-in. CSS owns the timing,
     the stagger delays and the html.js-scoped hiding, so no-JS pages never hide
     anything. One shared observer, one-shot per element. */
  (() => {
    const A = W.Atar;
    const show = (n) => n.classList.add('is-revealed');
    /* Includes the root itself, not just descendants — shopify:section:load can hand
       us an element that is itself the reveal target. */
    const pending = (root) => {
      const r = root && root.querySelectorAll ? root : D;
      const list = Array.prototype.slice.call(r.querySelectorAll('[data-animate]:not(.is-revealed)'));
      if (r.matches && r.matches('[data-animate]:not(.is-revealed)')) list.unshift(r);
      return list;
    };
    let io = null;

    /* Stands down the CSS failsafe in base.css — see the .reveal-ready note there. */
    D.documentElement.classList.add('reveal-ready');

    /* Re-scan hook for markup rendered after load (theme editor, injected sections). */
    A.reveal = (root) => {
      pending(root).forEach((n) => {
        if (io && !n.hasAttribute('data-no-animate')) io.observe(n); else show(n);
      });
    };
    D.addEventListener('shopify:section:load', (e) => A.reveal(e.target));

    if (A.reducedMotion() || typeof IntersectionObserver !== 'function') {
      A.ready(() => A.reveal()); /* everything visible at once — no observer built */
      return;
    }

    /* Root grown at the bottom only (vertical = direction-agnostic in RTL) so a
       reveal starts just before its element scrolls into view. */
    io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        show(entry.target);
      });
    }, { rootMargin: '0px 0px 8% 0px', threshold: 0.01 });

    A.ready(() => A.reveal());
  })();
})();


/* ── Looping product carousel ────────────────────────────────────────────────
   Progressive enhancement: the track is a native scroll-snap list and already
   swipes without JS. This adds arrow buttons and wraps around at both ends. */
(function () {
  function step(track) {
    var first = track.querySelector('li');
    if (!first) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function init(root) {
    var track = root.querySelector('[data-carousel-track]');
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    if (!track) return;

    function maxScroll() { return track.scrollWidth - track.clientWidth; }

    function go(dir) {
      var d = step(track) * dir;
      var target = track.scrollLeft + d;
      var max = maxScroll();
      // loop: past the end returns to the start, before the start jumps to the end
      if (dir > 0 && track.scrollLeft >= max - 2) target = 0;
      else if (dir < 0 && track.scrollLeft <= 2) target = max;
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
    });

    function sync() {
      var scrollable = maxScroll() > 4;
      [prev, next].forEach(function (b) { if (b) b.hidden = !scrollable; });
    }
    sync();
    window.addEventListener('resize', sync);

    /* Auto-advance, wrapping back to the start at the end. Pauses on hover,
       on focus, while the user is touching it, and when the tab is hidden.
       Fully disabled for anyone who prefers reduced motion. */
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timer = null, paused = false;

    function tick() { if (!paused && maxScroll() > 4 && !document.hidden) go(1); }
    function start() {
      if (reduce.matches || timer) return;
      timer = setInterval(tick, 5000);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    ['mouseenter', 'focusin', 'touchstart'].forEach(function (e) {
      root.addEventListener(e, function () { paused = true; }, { passive: true });
    });
    ['mouseleave', 'focusout', 'touchend'].forEach(function (e) {
      root.addEventListener(e, function () { paused = false; }, { passive: true });
    });
    document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
    reduce.addEventListener && reduce.addEventListener('change', function () { reduce.matches ? stop() : start(); });
    start();
  }

  function boot() { document.querySelectorAll('[data-carousel]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
